import { inject, Injectable, isDevMode } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, takeUntil } from 'rxjs';
import { clearAllState, getHealthSummary } from '../../../../../core/store/actions/app.actions';
import { patientSwitchInitiated } from '../../../../../user/store/actions/user.actions';
import { selectPatientId } from '../../../../../user/store/selectors/user.selectors';
import { PatientSurgicalHistory } from '../../../models/patient-surgical-history';
import { PatientSurgicalHistoriesService } from '../../../services/patient-surgical-histories/patient-surgical-histories.service';
import {
  getAllPatientSurgicalHistories,
  getAllPatientSurgicalHistoriesFailure,
  getAllPatientSurgicalHistoriesSuccess,
  getPatientSurgicalHistoriesFailure,
  getPatientSurgicalHistoriesSuccess,
  loadPatientSurgicalHistories,
  recordPatientSurgicalHistoryPrintEvent,
  recordPatientSurgicalHistoryPrintEventFailure,
  recordPatientSurgicalHistoryPrintEventSuccess,
} from '../actions/patient-surgical-history.actions';
import {
  selectCacheLoadedAt,
  selectCachePatientId,
  selectCurrentCount,
  selectIsAtEndOfData,
  selectIsFullDatasetLoaded,
  selectPageNumber,
  selectPageSize,
  selectPatientSurgicalHistories,
} from '../selectors/patient-surgical-history.selector';

@Injectable()
export class PatientSurgicalHistoryEffects {
  private readonly HEALTH_SUMMARY_WIDGET_PAGE_SIZE = 5;
  private readonly FULL_DATASET_CACHE_TTL_MS = 5 * 60 * 1000;
  private actions$ = inject(Actions);
  private patientSurgicalHistoriesService = inject(PatientSurgicalHistoriesService);
  private store = inject(Store);
  private readonly cancelRequest$ = this.actions$.pipe(ofType(patientSwitchInitiated, clearAllState));

  getPatientSurgicalHistories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadPatientSurgicalHistories),
      concatLatestFrom(() => [
        this.store.select(selectPatientId),
        this.store.select(selectCurrentCount),
        this.store.select(selectPageSize),
        this.store.select(selectIsFullDatasetLoaded),
        this.store.select(selectCachePatientId),
        this.store.select(selectCacheLoadedAt),
        this.store.select(selectPatientSurgicalHistories),
      ]),
      mergeMap(
        ([
          { pageNumber, pageSizeOverride },
          patientId,
          currentCount,
          pageSize,
          isFullDatasetLoaded,
          cachePatientId,
          cacheLoadedAt,
          cachedPatientSurgicalHistories,
        ]) => {
          const requestPageSize = pageSizeOverride ?? pageSize;

          const shouldUseFullDatasetCache =
            patientId != null &&
            isFullDatasetLoaded &&
            cachePatientId === patientId &&
            this.isCacheFresh(cacheLoadedAt);
          if (shouldUseFullDatasetCache) {
            if (isDevMode()) {
              console.debug('[Surgical History][Cache] Full-dataset cache hit; skipping paged API call.', {
                patientId,
                currentCount,
              });
            }
            return of(
              getPatientSurgicalHistoriesSuccess({
                patientSurgicalHistories: cachedPatientSurgicalHistories,
                pageNumber: 1,
                isAtEndOfData: true,
                totalRecordCount: currentCount,
              })
            );
          }

          return this.patientSurgicalHistoriesService
            .postPatientSurgicalHistories(patientId, pageNumber, requestPageSize)
            .pipe(
              takeUntil(this.cancelRequest$),
              map(response => this.toSuccessAction(response, pageNumber, currentCount)),
              catchError(err => of(getPatientSurgicalHistoriesFailure({ error: err })))
            );
        }
      )
    );
  });

  loadOnGetHealthSummary$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getHealthSummary),
      concatLatestFrom(() => [
        this.store.select(selectCurrentCount),
        this.store.select(selectPatientSurgicalHistories),
        this.store.select(selectPageNumber),
        this.store.select(selectIsAtEndOfData),
        this.store.select(selectPatientId),
      ]),
      mergeMap(([, currentCount, patientSurgicalHistories, pageNumber, isAtEndOfData, patientId]) => {
        if (currentCount > 0) {
          if (isDevMode()) {
            console.debug('[Surgical History][Cache] Health summary cache hit; skipping API call.', {
              patientId,
              currentCount,
            });
          }
          return of(
            getPatientSurgicalHistoriesSuccess({
              patientSurgicalHistories,
              pageNumber: pageNumber || 1,
              isAtEndOfData: isAtEndOfData ?? false,
              totalRecordCount: currentCount,
            })
          );
        }

        return of(
          loadPatientSurgicalHistories({
            pageNumber: 1,
            pageSizeOverride: this.HEALTH_SUMMARY_WIDGET_PAGE_SIZE,
          })
        );
      })
    );
  });

  getAllPatientSurgicalHistories$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getAllPatientSurgicalHistories),
      concatLatestFrom(() => [
        this.store.select(selectIsFullDatasetLoaded),
        this.store.select(selectCachePatientId),
        this.store.select(selectCacheLoadedAt),
        this.store.select(selectPatientSurgicalHistories),
      ]),
      mergeMap(([{ patientId }, isFullDatasetLoaded, cachePatientId, cacheLoadedAt, patientSurgicalHistories]) => {
        if (isFullDatasetLoaded && cachePatientId === patientId && this.isCacheFresh(cacheLoadedAt)) {
          if (isDevMode()) {
            console.debug('[Surgical History][Cache] Full-dataset cache hit; skipping GetAllByPatientId call.', {
              patientId,
              count: patientSurgicalHistories.length,
            });
          }
          return of(
            getAllPatientSurgicalHistoriesSuccess({
              patientSurgicalHistories,
              patientId,
              loadedAt: cacheLoadedAt ?? Date.now(),
            })
          );
        }

        return this.patientSurgicalHistoriesService.getAllPatientSurgicalHistoriesByPatientId(patientId).pipe(
          takeUntil(this.cancelRequest$),
          map(patientSurgicalHistories =>
            getAllPatientSurgicalHistoriesSuccess({
              patientSurgicalHistories,
              patientId,
              loadedAt: Date.now(),
            })
          ),
          catchError(err => of(getAllPatientSurgicalHistoriesFailure({ error: err })))
        );
      })
    );
  });

  private toSuccessAction(
    response: { result: PatientSurgicalHistory[] | null; totalRecordCount: number },
    pageNumber = 1,
    currentCount = 0
  ) {
    const patientSurgicalHistories = response.result ?? [];
    const totalRecordCount = response.totalRecordCount;
    const nextPage = pageNumber || 1;
    const isNoData = patientSurgicalHistories.length === 0;
    const isAtEndOfData = isNoData || currentCount + patientSurgicalHistories.length === totalRecordCount;

    return getPatientSurgicalHistoriesSuccess({
      patientSurgicalHistories,
      pageNumber: isNoData ? Math.max(nextPage - 1, 1) : nextPage,
      isAtEndOfData,
      totalRecordCount,
    });
  }

  recordPrintEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(recordPatientSurgicalHistoryPrintEvent),
      mergeMap(({ patientId, patientSurgeryIds }) =>
        this.patientSurgicalHistoriesService.postPrintEvent(patientId, patientSurgeryIds).pipe(
          map(() => recordPatientSurgicalHistoryPrintEventSuccess()),
          catchError(err => of(recordPatientSurgicalHistoryPrintEventFailure({ error: err })))
        )
      )
    );
  });

  private isCacheFresh(cacheLoadedAt: number | null): boolean {
    if (!cacheLoadedAt) {
      return false;
    }

    return Date.now() - cacheLoadedAt <= this.FULL_DATASET_CACHE_TTL_MS;
  }
}
